const PROFILE_EXTRAS_PREFIX = 'hrv4.profileExtras';
const PROFILE_EXTRAS_EVENT_NAME = 'hrv4:profile-extras-updated';

export const TRAINING_BACKGROUND_OPTIONS = [
  { value: 10, label: 'Beginner', description: 'New to structured training or returning after a long break.' },
  { value: 20, label: 'Developing', description: 'Training occasionally with light consistency.' },
  { value: 30, label: 'Consistent', description: 'Training regularly with an established routine.' },
  { value: 40, label: 'Experienced', description: 'Several years of steady training with clear goals.' },
  { value: 50, label: 'Advanced', description: 'High training load with strong familiarity and discipline.' },
  { value: 60, label: 'Competitive', description: 'Structured training with performance-oriented or competitive focus.' },
];

export const AVATAR_OPTIONS = [
  { id: 'letter', emoji: null, label: 'Letter' },
  { id: 'cat', emoji: '🐱', label: 'Cat' },
  { id: 'dog', emoji: '🐶', label: 'Dog' },
  { id: 'bunny', emoji: '🐰', label: 'Bunny' },
  { id: 'bear', emoji: '🐻', label: 'Bear' },
  { id: 'panda', emoji: '🐼', label: 'Panda' },
  { id: 'koala', emoji: '🐨', label: 'Koala' },
  { id: 'fox', emoji: '🦊', label: 'Fox' },
  { id: 'lion', emoji: '🦁', label: 'Lion' },
  { id: 'monkey', emoji: '🐵', label: 'Monkey' },
  { id: 'frog', emoji: '🐸', label: 'Frog' },
  { id: 'penguin', emoji: '🐧', label: 'Penguin' },
  { id: 'owl', emoji: '🦉', label: 'Owl' },
  { id: 'unicorn', emoji: '🦄', label: 'Unicorn' },
  { id: 'dragon', emoji: '🐲', label: 'Dragon' },
  { id: 'flower', emoji: '🌸', label: 'Flower' },
  { id: 'star', emoji: '⭐', label: 'Star' },
  { id: 'sun', emoji: '☀️', label: 'Sun' },
  { id: 'moon', emoji: '🌙', label: 'Moon' },
  { id: 'rainbow', emoji: '🌈', label: 'Rainbow' },
  { id: 'fire', emoji: '🔥', label: 'Fire' },
];

function getProfileExtrasKey(userId) {
  return `${PROFILE_EXTRAS_PREFIX}.${userId || 'guest'}`;
}

function emitProfileExtrasUpdate(detail) {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(PROFILE_EXTRAS_EVENT_NAME, { detail }));
}

function isValidDateString(value) {
  if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const date = new Date(`${value}T00:00:00`);
  return !Number.isNaN(date.getTime());
}

export function splitDateOfBirth(dateOfBirth) {
  if (!isValidDateString(dateOfBirth)) {
    return { birthDay: '', birthMonth: '', birthYear: '' };
  }

  const [birthYear, birthMonth, birthDay] = dateOfBirth.split('-');
  return { birthDay, birthMonth, birthYear };
}

export function joinDateOfBirth({ birthYear, birthMonth, birthDay }) {
  if (!birthYear || !birthMonth || !birthDay) return '';
  return `${birthYear}-${String(birthMonth).padStart(2, '0')}-${String(birthDay).padStart(2, '0')}`;
}

export function calculateAgeFromDateOfBirth(dateOfBirth, referenceDate = new Date()) {
  if (!isValidDateString(dateOfBirth)) return null;
  const birthDate = new Date(`${dateOfBirth}T00:00:00`);
  const refDate = new Date(referenceDate);

  let age = refDate.getFullYear() - birthDate.getFullYear();
  const monthDiff = refDate.getMonth() - birthDate.getMonth();

  if (monthDiff < 0 || (monthDiff === 0 && refDate.getDate() < birthDate.getDate())) {
    age -= 1;
  }

  return age >= 0 ? age : null;
}

export function formatDateOfBirth(dateOfBirth) {
  if (!isValidDateString(dateOfBirth)) return '—';
  return new Date(`${dateOfBirth}T00:00:00`).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

export function getTrainingBackgroundLabel(value) {
  return TRAINING_BACKGROUND_OPTIONS.find((option) => option.value === Number(value))?.label || '—';
}

export function loadProfileExtras(userId) {
  if (typeof window === 'undefined') {
    return { dateOfBirth: '', birthDay: '', birthMonth: '', birthYear: '', profilePhoto: '', avatarId: 'letter' };
  }

  try {
    const raw = window.localStorage.getItem(getProfileExtrasKey(userId));
    if (!raw) return { dateOfBirth: '', birthDay: '', birthMonth: '', birthYear: '', profilePhoto: '', avatarId: 'letter' };

    const parsed = JSON.parse(raw);
    const avatarId = typeof parsed.avatarId === 'string' && AVATAR_OPTIONS.some((option) => option.id === parsed.avatarId)
      ? parsed.avatarId
      : 'letter';
    const dateOfBirth = typeof parsed.dateOfBirth === 'string' ? parsed.dateOfBirth : '';
    const split = splitDateOfBirth(dateOfBirth);

    return {
      dateOfBirth,
      birthDay: typeof parsed.birthDay === 'string' && parsed.birthDay ? parsed.birthDay.padStart(2, '0') : split.birthDay,
      birthMonth: typeof parsed.birthMonth === 'string' && parsed.birthMonth ? parsed.birthMonth.padStart(2, '0') : split.birthMonth,
      birthYear: typeof parsed.birthYear === 'string' ? parsed.birthYear : split.birthYear,
      profilePhoto: typeof parsed.profilePhoto === 'string' ? parsed.profilePhoto : '',
      avatarId,
    };
  } catch {
    return { dateOfBirth: '', birthDay: '', birthMonth: '', birthYear: '', profilePhoto: '', avatarId: 'letter' };
  }
}

export function saveProfileExtras(userId, extras) {
  if (typeof window === 'undefined') return;

  const existing = loadProfileExtras(userId);
  const nextValue = {
    ...existing,
    ...extras,
  };

  try {
    const previous = JSON.stringify(existing);
    const next = JSON.stringify(nextValue);
    if (previous === next) return;
    window.localStorage.setItem(getProfileExtrasKey(userId), next);
    emitProfileExtrasUpdate({ userId, extras: nextValue });
  } catch {
    // Ignore storage quota failures.
  }
}

export function subscribeToProfileExtrasUpdates(callback) {
  if (typeof window === 'undefined') return () => {};
  window.addEventListener(PROFILE_EXTRAS_EVENT_NAME, callback);
  return () => window.removeEventListener(PROFILE_EXTRAS_EVENT_NAME, callback);
}